import { addBreadcrumb } from './sentry';

export type SubmitTracePhase =
  | 'click'
  | 'blocked_isSubmitting'
  | 'validate'
  | 'upload'
  | 'notify'
  | 'auth_start'
  | 'auth_cache_hit'
  | 'auth_session'
  | 'auth_jwt'
  | 'auth_fail'
  | 'fetch_start'
  | 'fetch_end'
  | 'fetch_fail'
  | 'job_id_set'
  | 'poll_start'
  | 'cancelled';

export type SubmitTraceEvent = {
  phase: SubmitTracePhase;
  atMs: number;
  traceId: string;
  detail?: string;
};

const RING_MAX = 20;
const ring: SubmitTraceEvent[] = [];
let activeTraceId: string | null = null;
let traceSeq = 0;

const newTraceId = (): string => {
  traceSeq += 1;
  return `submit-${Date.now()}-${traceSeq}`;
};

export const isSubmitDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (localStorage.getItem('pixlie_submit_debug') === '1') return true;
    return new URLSearchParams(window.location.search).get('submit_debug') === '1';
  } catch {
    return false;
  }
};

export const beginSubmitTrace = (): string => {
  activeTraceId = newTraceId();
  ring.length = 0;
  return activeTraceId;
};

export const getActiveSubmitTraceId = (): string | null => activeTraceId;

export const submitTrace = (phase: SubmitTracePhase, detail?: string): void => {
  const traceId = activeTraceId ?? newTraceId();
  if (!activeTraceId) {
    activeTraceId = traceId;
  }
  const event: SubmitTraceEvent = {
    phase,
    atMs: typeof performance !== 'undefined' ? performance.now() : Date.now(),
    traceId,
    detail,
  };
  ring.push(event);
  if (ring.length > RING_MAX) {
    ring.shift();
  }
  console.info('[submit-trace]', phase, detail ?? '', { traceId });
  void addBreadcrumb('submit', phase, { traceId, detail });
};

export const getSubmitTraceRing = (): readonly SubmitTraceEvent[] => [...ring];

export const getLastSubmitTraceEvent = (): SubmitTraceEvent | null =>
  ring.length > 0 ? ring[ring.length - 1]! : null;

const PHASE_LABELS: Record<SubmitTracePhase, string> = {
  click: 'Submit clicked',
  blocked_isSubmitting: 'Blocked — submit already in progress',
  validate: 'Validating inputs',
  upload: 'Uploading source video',
  notify: 'Notification permission (non-blocking)',
  auth_start: 'Refreshing Appwrite session',
  auth_cache_hit: 'Using cached JWT',
  auth_session: 'Appwrite account.get',
  auth_jwt: 'Appwrite createJWT',
  auth_fail: 'Auth failed',
  fetch_start: 'POST /api/generate in flight',
  fetch_end: 'POST /api/generate completed',
  fetch_fail: 'POST /api/generate failed',
  job_id_set: 'Job id received',
  poll_start: 'Polling job status',
  cancelled: 'Submit cancelled',
};

export const getSubmitPhaseLabel = (phase: SubmitTracePhase, elapsedSec?: number): string => {
  const base = PHASE_LABELS[phase] ?? phase;
  if (elapsedSec != null && elapsedSec > 0) {
    return `${base} (${elapsedSec}s)`;
  }
  return base;
};

export const getLastPhaseLabel = (elapsedSec?: number): string => {
  const last = getLastSubmitTraceEvent();
  if (!last) return 'Idle — POST /api/generate not sent yet';
  return getSubmitPhaseLabel(last.phase, elapsedSec);
};

export const clearSubmitTrace = (): void => {
  activeTraceId = null;
  ring.length = 0;
};
